'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { partnerLinks, primaryNav, secondaryNav } from '@/data/movio';
import { getLenis, handleHashLinkClick } from '@/components/experience/smoothScroll';

/** Pages whose first screen is dark, so the header starts transparent with light text. */
const DARK_TOP_ROUTES = ['/homepage'];

export default function Header() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Light-on-dark styling over a dark first screen, and while the (dark) mobile menu is open.
  const overDark = menuOpen || (DARK_TOP_ROUTES.includes(pathname ?? '') && !scrolled);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = useCallback((restoreFocus = true) => {
    setMenuOpen(false);
    if (restoreFocus) toggleRef.current?.focus();
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Scroll lock, focus management and keyboard handling for the mobile menu.
  useEffect(() => {
    if (!menuOpen) return;
    const lenis = getLenis();
    lenis?.stop();
    document.body.style.overflow = 'hidden';
    const panel = panelRef.current;
    const focusables = () =>
      Array.from(panel?.querySelectorAll<HTMLElement>('a[href], button') ?? []);
    window.requestAnimationFrame(() => focusables()[0]?.focus());

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      lenis?.start();
    };
  }, [menuOpen, closeMenu]);

  const isActive = (href: string) =>
    !href.includes('#') && (pathname === href || pathname?.startsWith(href + '/'));

  const text = overDark
    ? 'text-white/80 hover:text-white'
    : 'text-foreground-muted hover:text-foreground';

  return (
    <>
      <header
        role="banner"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ease-v2 ${
          overDark
            ? 'border-b border-transparent bg-transparent'
            : 'border-b border-foreground/[0.06] bg-white/80 shadow-[0_8px_30px_-18px_rgba(15,23,42,0.25)] backdrop-blur-xl'
        }`}
      >
        <a
          href="#main-content"
          onClick={(e) => {
            // Inner pages render a plain <main>; focus whichever main landmark exists.
            const main = document.getElementById('main-content') ?? document.querySelector('main');
            if (!main) return;
            e.preventDefault();
            if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
            main.focus();
          }}
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:text-foreground"
        >
          Skip to content
        </a>
        <div
          className={`v2-container flex items-center justify-between transition-[height] duration-500 ease-v2 ${scrolled ? 'h-16' : 'h-20'}`}
        >
          <Link href="/homepage" aria-label="MOVIO home" className="flex shrink-0 items-center">
            <Image
              src="/assets/images/movio-logo.png"
              alt="MOVIO"
              width={132}
              height={40}
              priority
              className={`h-8 w-auto object-contain transition-[filter] duration-500 md:h-9 ${overDark ? 'brightness-0 invert' : ''}`}
            />
          </Link>

          <nav aria-label="Main navigation" className="hidden items-center gap-1 lg:flex">
            {primaryNav.map((link) =>
              link.dropdown ? (
                <div key={link.label} className="group relative">
                  <Link
                    href={link.href}
                    aria-haspopup="true"
                    className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors ${isActive(link.href) ? (overDark ? 'text-white' : 'text-primary') : text}`}
                  >
                    {link.label}
                    <Icon
                      name="ChevronDownIcon"
                      size={14}
                      className="transition-transform duration-300 group-focus-within:rotate-180 group-hover:rotate-180"
                    />
                  </Link>
                  <div className="invisible absolute left-0 top-full pt-3 opacity-0 transition-[opacity,visibility] duration-300 group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
                    <ul className="grid w-[30rem] grid-cols-2 gap-1 rounded-2xl border border-foreground/[0.06] bg-white p-3 shadow-v2-lift">
                      {link.dropdown.map((item) => (
                        <li key={item.href}>
                          <Link
                            href={item.href}
                            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground-muted transition-colors hover:bg-primary/5 hover:text-primary focus-visible:bg-primary/5 focus-visible:text-primary focus-visible:outline-none"
                          >
                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/[0.07]">
                              <Icon name={item.icon} size={14} className="text-primary" />
                            </span>
                            {item.label}
                          </Link>
                        </li>
                      ))}
                      <li className="col-span-2 mt-1 border-t border-foreground/[0.06] pt-2">
                        <Link
                          href="/products"
                          className="flex items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/5"
                        >
                          View all solutions
                          <Icon name="ArrowRightIcon" size={13} />
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleHashLinkClick(e, link.href)}
                  aria-current={isActive(link.href) ? 'page' : undefined}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${isActive(link.href) ? (overDark ? 'text-white' : 'text-primary') : text}`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              href={partnerLinks.become.href}
              className={`v2-btn hidden !min-h-0 !px-5 !py-2.5 text-sm sm:inline-flex ${overDark ? 'bg-white text-navy-900 hover:bg-glow-soft' : 'v2-btn-primary'}`}
            >
              {partnerLinks.become.label}
            </Link>
            <button
              ref={toggleRef}
              type="button"
              onClick={() => (menuOpen ? closeMenu() : setMenuOpen(true))}
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors lg:hidden ${
                overDark ? 'text-white hover:bg-white/10' : 'text-foreground hover:bg-foreground/5'
              }`}
            >
              <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        ref={panelRef}
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        data-open={menuOpen}
        className={`v2-menu-panel fixed inset-0 z-40 flex flex-col overflow-y-auto bg-navy-950 px-6 pb-10 pt-24 text-white lg:hidden ${
          menuOpen ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-3 opacity-0'
        }`}
      >
        <nav aria-label="Mobile navigation">
          <ul className="space-y-1">
            {primaryNav.map((link) => (
              <li key={link.label}>
                <Link
                  href={link.href}
                  onClick={(e) => {
                    handleHashLinkClick(e, link.href);
                    closeMenu(false);
                  }}
                  className="block py-2.5 text-[1.7rem] font-semibold tracking-tight text-white/90 transition-colors hover:text-glow focus-visible:text-glow focus-visible:outline-none"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <ul className="mt-8 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/10 pt-6">
            {secondaryNav.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className="block py-1.5 text-sm text-white/60 hover:text-white"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <Link href={partnerLinks.become.href} className="v2-btn v2-btn-primary mt-auto w-full">
          {partnerLinks.become.label}
        </Link>
      </div>
    </>
  );
}
